/**
 * Serialization boundary for chat and submission attachments.
 *
 * Attachments live in `workspace_messages.metadata.attachments` rather than a
 * join table: they are always fetched with their message, never queried alone,
 * and never number more than a handful. See the design spec for the full
 * argument.
 *
 * Everything here is pure. Metadata arriving from the database is
 * user-influenced JSON, so parsing is defensive: a malformed entry is dropped,
 * never thrown on, so a broken attachment cannot blank a message's text.
 */

import { PEAK_BUCKETS } from "./audio";
import { isShareableType } from "./uploadTypes";

export interface MessageAttachment {
  /** Storage path within the `quest-files` bucket, e.g. `{questId}/chat/{ts}-{name}`. */
  path: string;
  name: string;
  mime: string;
  size: number;
  /** Images and video only. Reserved at render time so loading causes no reflow. */
  width?: number;
  height?: number;
  /** Audio only. Duration in milliseconds. */
  durationMs?: number;
  /** Audio only. Normalized waveform peaks for visualization. */
  peaks?: number[];
}

export const MAX_IMAGE_BYTES = 10_485_760; // 10 MB
export const MAX_VIDEO_BYTES = 52_428_800; // 50 MB
export const MAX_AUDIO_BYTES = 16_777_216; // 16 MB

const ATTACHMENTS_KEY = "attachments";

function isMessageAttachment(value: unknown): value is MessageAttachment {
  if (typeof value !== "object" || value === null) return false;
  const a = value as Record<string, unknown>;
  return (
    typeof a.path === "string" &&
    a.path.length > 0 &&
    typeof a.name === "string" &&
    typeof a.mime === "string" &&
    typeof a.size === "number"
  );
}

/** Narrow a raw entry to exactly the known fields — never pass unknown keys through. */
function normalize(a: MessageAttachment): MessageAttachment {
  const out: MessageAttachment = {
    path: a.path,
    name: a.name,
    mime: a.mime,
    size: a.size,
  };
  if (typeof a.width === "number") out.width = a.width;
  if (typeof a.height === "number") out.height = a.height;
  // NaN and Infinity both satisfy `typeof === "number"`, and this value comes
  // from member-writable metadata, so finiteness is checked rather than assumed.
  if (typeof a.durationMs === "number" && Number.isFinite(a.durationMs) && a.durationMs >= 0) {
    out.durationMs = a.durationMs;
  }
  const peaks = sanitisePeaks(a.peaks);
  if (peaks) out.peaks = peaks;
  return out;
}

/**
 * Peaks arrive from `workspace_messages.metadata`, which any member of a
 * non-frozen quest can write, and VoiceNote renders one DOM node per entry.
 * An unbounded array is therefore a resource-exhaustion vector aimed at the
 * OTHER party's browser, not just malformed data — so the length is capped
 * here rather than trusted and every entry is proven to be a finite number in
 * 0..1 before it can reach a style attribute.
 *
 * Returns undefined when nothing usable survives: a voice note with unusable
 * peaks must still parse and still play, just without a waveform.
 */
function sanitisePeaks(raw: unknown): number[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const peaks: number[] = [];
  for (const value of raw) {
    if (peaks.length >= PEAK_BUCKETS) break;
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    peaks.push(Math.min(1, Math.max(0, value)));
  }
  return peaks.length > 0 ? peaks : undefined;
}

export function parseAttachments(
  metadata: Record<string, unknown> | null,
): MessageAttachment[] {
  if (!metadata) return [];
  const raw = metadata[ATTACHMENTS_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter(isMessageAttachment).map(normalize);
}

export function withAttachments(
  metadata: Record<string, unknown> | null,
  attachments: MessageAttachment[],
): Record<string, unknown> {
  return { ...(metadata ?? {}), [ATTACHMENTS_KEY]: attachments.map(normalize) };
}

export function attachmentKind(mime: string): "image" | "video" | "audio" | "file" {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
}

/**
 * The one kind every attachment on a message shares, or null when they are
 * mixed or there are none. Mirrors get_workspace_inbox's
 * last_message_attachment_kind (20261001140000) for the pre-migration path.
 */
export function uniformAttachmentKind(
  metadata: Record<string, unknown> | null,
): ReturnType<typeof attachmentKind> | null {
  const kinds = new Set(parseAttachments(metadata).map((a) => attachmentKind(a.mime)));
  return kinds.size === 1 ? [...kinds][0]! : null;
}

export type AttachmentProblem = "empty" | "unsupported_type" | "too_large";

export function validateAttachment(
  file: File,
): { ok: true } | { ok: false; reason: AttachmentProblem } {
  if (file.size === 0) return { ok: false, reason: "empty" };
  // The bucket would refuse it after the upload; say so while it's staged.
  if (!isShareableType(file.type)) return { ok: false, reason: "unsupported_type" };
  const kind = attachmentKind(file.type);
  let cap: number;
  if (kind === "image") {
    cap = MAX_IMAGE_BYTES;
  } else if (kind === "video") {
    cap = MAX_VIDEO_BYTES;
  } else if (kind === "audio") {
    cap = MAX_AUDIO_BYTES;
  } else {
    cap = MAX_VIDEO_BYTES;
  }
  if (file.size > cap) return { ok: false, reason: "too_large" };
  return { ok: true };
}
